/* eslint-disable react/display-name */
import ClipboardButton from "./ClipboardButton";
import LazyLoad from "react-lazyload";
import { Link } from "react-router-dom";
import { OutboundLink } from "react-ga";
import PropTypes from "prop-types";
import React from "react";
import Skeleton from "react-loading-skeleton";
import bannedImg from "../assets/banned.png";
import classNames from "classnames";
import salt_48 from "../assets/salt_48.png";
import styles from "../styles/Card.module.scss";
import unofficialImg from "../assets/unofficial.png";
import useDarkMode from "use-dark-mode";
import { useSelector } from "react-redux";

const LinkComponent = ({ children, url }) => {
  return url ? <Link to={url}>{children}</Link> : <>{children}</>;
};

LinkComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  url: PropTypes.string,
};

const Card = (props) => {
  const priceSources = useSelector((state) => state.settings.priceSources);
  const darkMode = useDarkMode(false);

  if (!props) {
    return <div>&nbsp;</div>;
  }

  const {
    background,
    banned,
    hideName,
    image_uris,
    isLoading,
    label,
    layout,
    name,
    names,
    prices,
    salt,
    style,
    unofficial,
    url,
  } = props;

  const chUrl = prices?.cardhoarder?.url.startsWith("https://")
    ? prices.cardhoarder.url
    : `https://www.cardhoarder.com/cards/${prices?.cardhoarder?.url}?affiliate_id=edhrec`;
  const ckUrl = prices?.cardkingdom?.url.startsWith("https://")
    ? prices.cardkingdom.url
    : `https://www.cardkingdom.com/mtg/${prices?.cardkingdom?.url}?partner=edhrec&utm_source=edhrec&utm_medium=affiliate&utm_campaign=edhrec`;
  const cmUrl = prices?.cardmarket?.url.startsWith("https://")
    ? prices.cardmarket.url
    : `https://www.cardmarket.com/en/Magic/Cards/${prices?.cardmarket?.url}?utm_source=edhrec&utm_medium=text&utm_campaign=card_prices`;
  const csiUrl = prices?.coolstuffinc?.url.startsWith("https://")
    ? prices.coolstuffinc.url
    : `http://www.coolstuffinc.com/p/Magic%3A+The+Gathering/${prices?.coolstuffinc?.url}?utm_source=edhrec`;
  const tcgpUrl = prices?.tcgplayer?.url.startsWith("https://")
    ? prices.tcgplayer.url
    : `https://store.tcgplayer.com/magic/${prices?.tcgplayer?.url}?partner=EDHREC&utm_campaign=affiliate&utm_medium=EDHREC&utm_source=EDHREC`;

  const priceLinks = {
    cardhoarder: () => (
      <OutboundLink
        alt="Buy at Cardhoarder"
        eventLabel={chUrl}
        target="_blank"
        title="Buy at Cardhoarder"
        to={chUrl}
      >
        <div className={styles.price}>
          <span>{`${prices.cardhoarder.price} TIX`}</span>
        </div>
      </OutboundLink>
    ),
    cardkingdom: () => (
      <OutboundLink
        alt="Buy at Card Kingdom"
        eventLabel={ckUrl}
        target="_blank"
        title="Buy at Card Kingdom"
        to={ckUrl}
      >
        <div className={styles.price}>
          <svg
            height="12"
            version="1.1"
            viewBox="0 0 3.7041667 3.1750001"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="translate(0 -293.82)">
              <path d="m0 293.82v1.1592h0.52042v2.0158h2.6633v-2.0158h0.52042v-1.1592h-0.57636v1.0908h-0.49196v-1.084h-0.54839v1.084h-0.47074v-1.084h-0.54839v1.084h-0.49196v-1.0908z" />
            </g>
          </svg>
          <span>{`$${prices.cardkingdom.price.toFixed(2)}`}</span>
        </div>
      </OutboundLink>
    ),
    cardmarket: () => (
      <OutboundLink
        alt="Buy at Cardmarket"
        eventLabel={cmUrl}
        target="_blank"
        title="Buy at Cardmarket"
        to={cmUrl}
      >
        <div className={styles.price}>
          <span>
            {`${
              typeof prices.cardmarket.price === "number"
                ? prices.cardmarket.price.toFixed(2)
                : prices.cardmarket.price
            } €`.replace(".", ",")}
          </span>
        </div>
      </OutboundLink>
    ),
    coolstuffinc: () => (
      <OutboundLink
        alt="Buy at CoolStuffInc"
        eventLabel={csiUrl}
        target="_blank"
        title="Buy at CoolStuffInc"
        to={csiUrl}
      >
        <div className={styles.price}>
          <svg
            width="24"
            height="12"
            version="1.1"
            viewBox="0 0 24 12"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="matrix(.056341 0 0 .056341 -.70313 -6.6902)">
              <path d="m176.83 322.32c-17 6.11-36.63 9.17-58.89 9.17-21.88 0-40.74-4.18-56.6-12.54s-27.97-20.25-36.32-35.68c-8.36-15.43-12.54-33.22-12.54-53.37 0-21.68 4.68-40.98 14.04-57.89s22.59-29.97 39.69-39.19 36.77-13.83 59.03-13.83c17.77 0 34.96 2.15 51.58 6.45v57.17c-5.73-3.44-12.56-6.11-20.49-8.02s-15.95-2.87-24.07-2.87c-16.81 0-30.07 4.82-39.76 14.47-9.7 9.65-14.54 22.74-14.54 39.26 0 16.43 4.85 29.4 14.54 38.9 9.69 9.51 22.66 14.26 38.9 14.26 15 0 30.14-3.82 45.42-11.46v55.17z" />
              <path d="m351.49 265.72c0 13.66-3.65 25.55-10.96 35.68s-17.55 17.67-30.73 22.64-29.04 7.45-47.57 7.45c-21.49 0-41.17-3.63-59.03-10.89v-56.31c8.6 6.3 18.1 11.39 28.51 15.26s20.11 5.8 29.09 5.8c6.78 0 12.04-1.22 15.76-3.65 3.72-2.44 5.59-5.99 5.59-10.67 0-3.34-0.93-6.28-2.79-8.81s-4.68-4.89-8.45-7.09c-3.78-2.2-10.77-5.21-20.99-9.03-33.05-12.7-49.58-33.24-49.58-61.61 0-19.87 7.54-35.75 22.64-47.64 15.09-11.89 35.34-17.84 60.75-17.84 7.16 0 13.76 0.29 19.77 0.86s11.48 1.29 16.41 2.15c4.92 0.86 11.82 2.48 20.71 4.87v52.3c-17.29-9.46-34.29-14.19-51.01-14.19-6.88 0-12.42 1.29-16.62 3.87-4.21 2.58-6.3 6.11-6.3 10.6 0 4.3 1.7 7.86 5.09 10.67 3.39 2.82 10.48 6.47 21.28 10.96 21.3 8.6 36.37 18.01 45.21 28.23 8.8 10.22 13.22 22.35 13.22 36.39z" />
              <path d="m376.56 327.9v-205.47h61.9v205.47z" />
            </g>
          </svg>
          <span>{`$${prices.coolstuffinc.price}`}</span>
        </div>
      </OutboundLink>
    ),
    tcgplayer: () => (
      <OutboundLink
        alt="Buy at TCGplayer"
        eventLabel={tcgpUrl}
        rel="noreferrer"
        target="_blank"
        title="Buy at TCGplayer"
        to={tcgpUrl}
      >
        <div className={styles.price}>
          <svg
            aria-hidden="true"
            className=""
            focusable="false"
            height="12"
            version="1.1"
            viewBox="0 0 10.399999 12"
            width="10.4"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m4.22 0.8h-2.5536c-0.4496 0-0.8 0.3576-0.8 0.8v8.8c0 0.4424 0.3584 0.8 0.8 0.8h7.0672c0.4496 0 0.8-0.3576 0.8-0.8v-8.8c9e-7 -0.4424-0.3584-0.8-0.8-0.8h-1.4896l0.5472 2.52c0.025596 0.0768-0.00804 0.14-0.1008 0.188-0.0672 0.0352-0.1472 0.0528-0.24 0.0528-0.041604 0-0.072 0-0.092796-4e-3l-2.2712 0.9552 0.656 3.2432c0.020004 0.051204 0.00804 0.099204-0.036 0.144-0.0432 0.044796-0.1096 0.075204-0.1968 0.0912-0.051996 0.00636-0.087996 0.0096-0.108 0.0096-0.1496 0-0.2584-0.039996-0.3256-0.12l-2.444-3.9624c-0.056-0.0776-0.0384-0.148 0.0544-0.212 0.0776-0.048 0.1648-0.072 0.264-0.072 0.02 0 0.0504 0.0032 0.092 0.0096l2.1968-1.128-1.02-1.7152zm-4.22 0.005604c0-0.4456 0.3576-0.8056 0.8-0.8056h8.8c0.4424 0 0.8 0.36 0.8 0.8056v10.389c0 0.4456-0.3576 0.8056-0.8 0.8056h-8.8c-0.4424 0-0.8-0.36-0.8-0.8056z"
              fillRule="evenodd"
              strokeWidth=".8"
            />
          </svg>
          <span>{`$${prices.tcgplayer.price.toFixed(2)}`}</span>
        </div>
      </OutboundLink>
    ),
  };

  return (
    <div className={classNames(styles.container, { [styles.dark]: darkMode.value })} style={style}>
      {banned ? (
        <div className={styles.banned}>
          <img alt="Banned" src={bannedImg} />
        </div>
      ) : unofficial ? (
        <div className={styles.banned}>
          <img alt="Unofficial" src={unofficialImg} />
        </div>
      ) : null}
      <div className={styles.imageContainer} style={{ height: 340 }}>
        <ClipboardButton cardProps={props} layout={layout} name={name} names={names} />
        <LinkComponent url={url}>
          {image_uris && image_uris.length === 2 ? (
            <>
              <LazyLoad height={306}>
                <img
                  alt={name}
                  className={classNames(styles.image, styles.imageFront, "shadow")}
                  src={
                    image_uris[0].normal
                      ? image_uris[0].normal
                      : image_uris[0].startsWith("https://")
                      ? image_uris[0]
                      : `https://c1.scryfall.com/file/scryfall-cards/normal/front/${image_uris[0]}`
                  }
                />
              </LazyLoad>
              <LazyLoad height={306}>
                <img
                  alt={name}
                  className={classNames(styles.image, styles.imageBack, "shadow")}
                  src={
                    image_uris[1].normal
                      ? image_uris[1].normal
                      : image_uris[1].startsWith("https://")
                      ? image_uris[1]
                      : `https://c1.scryfall.com/file/scryfall-cards/normal/front/${image_uris[1]}`
                  }
                />
              </LazyLoad>
            </>
          ) : (
            <LazyLoad height={340}>
              <img
                alt={name}
                className={classNames(styles.image, "shadow")}
                src={
                  image_uris === undefined
                    ? "https://c1.scryfall.com/file/scryfall-card-backs/large/59/597b79b3-7d77-4261-871a-60dd17403388.jpg?1561757283"
                    : image_uris[0].normal
                    ? image_uris[0].normal
                    : image_uris[0].startsWith("https://")
                    ? image_uris[0]
                    : `https://c1.scryfall.com/file/scryfall-cards/normal/front/${image_uris[0]}`
                }
              />
            </LazyLoad>
          )}
        </LinkComponent>
        {salt > 2 && (
          <div className={styles.salty}>
            <img alt="Salt Score" src={salt_48} />
            <div className={styles.saltyTooltip}>
              <img alt="Salt Score" src={salt_48} />
              &nbsp;High Salt Score: {salt.toFixed(2)}
            </div>
          </div>
        )}
      </div>
      <div className={classNames({ [styles.background]: background })}>
        <div className={styles.prices}>
          {prices && (
            <>
              {[0, 1, 2].map((i) => {
                return prices[priceSources[i]] && priceLinks[priceSources[i]] ? (
                  priceLinks[priceSources[i]]()
                ) : (
                  <span>&nbsp;</span>
                );
              })}
            </>
          )}
        </div>
        {!hideName && (
          <div className={styles.name}>{names && names.length > 0 ? names.join(" // ") : name}</div>
        )}
        <div className={styles.label}>{isLoading ? <Skeleton count={2} /> : label}</div>
      </div>
    </div>
  );
};

Card.propTypes = {
  background: PropTypes.bool,
  banned: PropTypes.bool,
  hideName: PropTypes.bool,
  image_uris: PropTypes.array,
  isLoading: PropTypes.bool,
  label: PropTypes.oneOf(PropTypes.string.isRequired, PropTypes.element.isRequired),
  layout: PropTypes.string,
  name: PropTypes.string,
  names: PropTypes.arrayOf(PropTypes.string),
  prices: PropTypes.shape({
    cardhoarder: PropTypes.shape({
      price: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    }),
    cardkingdom: PropTypes.shape({
      price: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    }),
    cardmarket: PropTypes.shape({
      price: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    }),
    coolstuffinc: PropTypes.shape({
      price: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    }),
    tcgplayer: PropTypes.shape({
      price: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    }),
  }),
  salt: PropTypes.number,
  style: PropTypes.string,
  unofficial: PropTypes.bool,
  url: PropTypes.string,
};

export default Card;
